import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  italianToday,
  type PlannedTransfer,
  type PlannedTransferRequest,
  SCHEDULE_DEFAULT_HORIZON,
  SCHEDULE_HORIZONS,
  type ScheduleAccount,
  type ScheduleBlock,
  type ScheduleOverrideRequest,
  type ScheduleResponse,
  type ScheduleSourceKind,
} from '@k-management/shared';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useListFilters } from '../../lib/list-filters.js';
import { PlannedTransferDialog } from './PlannedTransferDialog.js';
import { SCHEDULE_SORT_VALUES, ScheduleTable } from './ScheduleTable.js';
import { ScheduleTransfers } from './ScheduleTransfers.js';
import {
  BLOCK_LABELS,
  BLOCK_ORDER,
  filterRows,
  KIND_LABELS,
  SCHEDULE_DEFAULT_FILTERS,
  type ScheduleSide,
  scheduleCsv,
  showBalance,
} from './schedule-view.js';
import { announceTreasuryChange, worklistKeys } from './treasury-ui.js';

const HORIZON_LABELS: Record<number, string> = {
  30: '30 giorni',
  90: '90 giorni',
  180: '6 mesi',
  365: '12 mesi',
};

const SIDES: { value: ScheduleSide; label: string }[] = [
  { value: 'in', label: 'Attivo' },
  { value: 'out', label: 'Passivo' },
  { value: 'all', label: 'Entrambi' },
];

type TransferDialog =
  | { mode: 'new'; initial: Partial<PlannedTransferRequest> }
  | { mode: 'edit'; transfer: PlannedTransfer };

function AccountTile({ account }: { account: ScheduleAccount }) {
  const tone = account.worstTone === 'ok' ? '' : ` schedule-tile-${account.worstTone}`;
  return (
    <div className={`card kpi schedule-tile${tone}`}>
      <span className="muted small">
        {account.alias}
        {Number(account.creditLine) > 0 ? ` (fido ${formatMoney(account.creditLine)})` : ''}
      </span>
      <strong>{account.balance === null ? 'Saldo non noto' : formatMoney(account.balance)}</strong>
      <span className="small">
        {account.balanceDate
          ? `saldo al ${formatDate(account.balanceDate).slice(0, 5)}`
          : 'nessun estratto'}
        {account.stale && (
          <span className="warn-text"> — carica i movimenti per un saldo affidabile</span>
        )}
      </span>
      <span className="small">
        {account.lowest
          ? `Punto più basso ${formatMoney(account.lowest.amount)} il ${formatDate(account.lowest.date)}`
          : 'Nessun movimento previsto'}
      </span>
    </div>
  );
}

/**
 * Tesoreria › Scadenzario (3.25): what will be collected and paid, day by day,
 * with the balance foreseen per account. The Da sistemare list links to the
 * block to reschedule with `?blocco=da_riprogrammare`.
 */
export function SchedulePage() {
  usePageTitle('Scadenzario');
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const blockParam = params.get('blocco');
  const block = BLOCK_ORDER.includes(blockParam as ScheduleBlock)
    ? (blockParam as ScheduleBlock)
    : null;
  const { filters, setFilter, reset, changed, changedClass } = useListFilters(
    'schedule',
    SCHEDULE_DEFAULT_FILTERS,
  );
  const horizon = (SCHEDULE_HORIZONS as readonly number[]).includes(filters.horizon)
    ? filters.horizon
    : SCHEDULE_DEFAULT_HORIZON;
  const queryKey = ['treasury', 'schedule', horizon] as const;
  const suffix = `?horizon=${horizon}`;
  const schedule = useQuery({
    queryKey,
    queryFn: () => apiRequest<ScheduleResponse>(`/treasury/schedule${suffix}`),
    placeholderData: keepPreviousData,
  });
  const data = schedule.data;
  const canWrite = useCan('tesoreria.write');
  const canEdit = Boolean(data?.canEdit) && canWrite;

  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<TransferDialog | null>(null);
  const [removing, setRemoving] = useState<PlannedTransfer | null>(null);

  const write = useMutation({
    mutationFn: (call: { path: string; method: 'POST' | 'PUT' | 'DELETE'; body?: unknown }) =>
      apiRequest<ScheduleResponse>(`/treasury/schedule/${call.path}${suffix}`, {
        method: call.method,
        body: call.body,
      }),
    onSuccess: async (next) => {
      queryClient.setQueryData(queryKey, next);
      setError(null);
      announceTreasuryChange();
      // Other horizons and the count of Da sistemare are now stale.
      await queryClient.invalidateQueries({
        queryKey: ['treasury', 'schedule'],
        refetchType: 'none',
      });
      await queryClient.invalidateQueries({ queryKey: worklistKeys.all });
    },
    onError: (failure: Error) => setError(failure.message),
  });
  // Errors are shown by the page, so a failed write never leaves an unhandled rejection.
  const run = async (call: { path: string; method: 'POST' | 'PUT' | 'DELETE'; body?: unknown }) => {
    try {
      await write.mutateAsync(call);
      return true;
    } catch {
      return false;
    }
  };
  const override = async (request: ScheduleOverrideRequest) => {
    return run({ path: 'overrides', method: 'PUT', body: request });
  };

  const accounts = data?.accounts ?? [];
  const inView = filterRows(data?.rows ?? [], filters).filter(
    (row) => block === null || row.block === block,
  );
  const sorting = useTableSort('schedule', inView, SCHEDULE_SORT_VALUES);
  const withBalance = showBalance(filters, sorting.sort !== null) && block === null;

  const download = () => {
    const csv = scheduleCsv(sorting.rows, withBalance);
    const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `scadenzario-${italianToday()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const saveTransfer = async (body: PlannedTransferRequest) => {
    if (!dialog) return;
    const ok =
      dialog.mode === 'edit'
        ? await run({ path: `transfers/${dialog.transfer.id}`, method: 'PUT', body })
        : await run({ path: 'transfers', method: 'POST', body });
    if (ok) setDialog(null);
  };

  const alias = (code: string) => accounts.find((item) => item.code === code)?.alias ?? code;
  const kinds = Object.keys(KIND_LABELS) as ScheduleSourceKind[];

  return (
    <section className="stack">
      <p className="muted small">
        Solo ciò che è certo, nell’esistenza o anche nell’importo: fatture, pro-forma, rate di
        mutui, rateazioni, F24, rate e costi contrattualizzati delle commesse e dei progetti. Il
        saldo previsto è per conto, riga dopo riga.
      </p>

      <div className="card row between">
        <div className="row">
          <div className="row" role="group" aria-label="Lato">
            {SIDES.map((side) => (
              <button
                key={side.value}
                type="button"
                className={`small${filters.side === side.value ? ' primary' : ''}`}
                onClick={() => setFilter('side', side.value)}
              >
                {side.label}
              </button>
            ))}
          </div>
          <label>
            Fino a{' '}
            <select
              className={changedClass('horizon')}
              value={horizon}
              onChange={(event) => setFilter('horizon', Number(event.target.value))}
            >
              {SCHEDULE_HORIZONS.map((value) => (
                <option key={value} value={value}>
                  {HORIZON_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Conto{' '}
            <select
              className={changedClass('account')}
              value={filters.account}
              onChange={(event) => setFilter('account', event.target.value)}
            >
              <option value="">Tutti</option>
              {accounts.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.alias}
                </option>
              ))}
            </select>
          </label>
          <label>
            Tipo{' '}
            <select
              className={changedClass('kind')}
              value={filters.kind}
              onChange={(event) =>
                setFilter('kind', event.target.value as ScheduleSourceKind | 'all')
              }
            >
              <option value="all">Tutti</option>
              {kinds.map((kind) => (
                <option key={kind} value={kind}>
                  {KIND_LABELS[kind]}
                </option>
              ))}
            </select>
          </label>
          <label className="row">
            <input
              type="checkbox"
              checked={filters.noIntercompany}
              onChange={(event) => setFilter('noIntercompany', event.target.checked)}
            />
            Escludi intergruppo
          </label>
          <input
            type="search"
            placeholder="Cerca anagrafica o documento"
            value={filters.search}
            onChange={(event) => setFilter('search', event.target.value)}
          />
          {changed && (
            <button type="button" className="link" onClick={reset}>
              Azzera i filtri
            </button>
          )}
        </div>
        <button type="button" className="small" onClick={download} disabled={inView.length === 0}>
          Esporta CSV
        </button>
      </div>

      {block && (
        <p className="notice info small">
          Vedi solo il blocco «{BLOCK_LABELS[block]}».{' '}
          <button type="button" className="link" onClick={() => setParams({})}>
            Mostra tutto
          </button>
        </p>
      )}
      {!withBalance && data && (
        <p className="muted small">
          Il saldo previsto compare con «Entrambi», senza filtri di tipo, intergruppo o ricerca,
          {block ? ' senza il filtro sul blocco' : ''} e con l’ordine per data.
        </p>
      )}
      {schedule.isPending && <p className="muted">Caricamento…</p>}
      {schedule.isError && <p className="error">{schedule.error.message}</p>}
      {error && !dialog && !removing && <p className="error">{error}</p>}

      {data && (
        <>
          <div className="kpi-row">
            {accounts.map((account) => (
              <AccountTile key={account.code} account={account} />
            ))}
            <div
              className={`card kpi schedule-tile${data.toReschedule > 0 ? ' schedule-tile-oltre_fido' : ''}`}
            >
              <span className="muted small">Da riprogrammare</span>
              <strong>
                {data.toReschedule > 0
                  ? `${data.toReschedule} ${data.toReschedule === 1 ? 'scadenza' : 'scadenze'}`
                  : 'nessuna'}
              </strong>
              {data.toReschedule > 0 && block !== 'da_riprogrammare' && (
                <Link to="/tesoreria/scadenzario?blocco=da_riprogrammare" className="small">
                  Mostra solo queste
                </Link>
              )}
            </div>
          </div>

          {data.toReschedule > 0 && (
            <p className="notice small">
              <strong>Saldo previsto non affidabile:</strong> {data.toReschedule}{' '}
              {data.toReschedule === 1 ? 'scadenza è passata' : 'scadenze sono passate'} da più di{' '}
              {data.tolerance} giorni senza una nuova data. Finché non le riprogrammi, il saldo le
              conta a oggi.
            </p>
          )}
          {data.suggestions.map((item) => (
            <div
              key={`${item.fromCode}-${item.toCode}`}
              className="notice error-notice small row between"
            >
              <span>
                <strong>
                  {alias(item.toCode)} va in sconfinamento entro il {formatDate(item.by)}
                </strong>
                , mentre {alias(item.fromCode)} ha disponibilità. Serve un giroconto da{' '}
                {alias(item.fromCode)} a {alias(item.toCode)} di almeno {formatMoney(item.amount)}{' '}
                entro il {formatDate(item.by)}.
              </span>
              {canEdit && (
                <button
                  type="button"
                  className="small primary"
                  onClick={() => {
                    setError(null);
                    setDialog({
                      mode: 'new',
                      initial: {
                        fromCode: item.fromCode,
                        toCode: item.toCode,
                        amount: item.amount,
                        date: item.by,
                      },
                    });
                  }}
                >
                  Programma il giroconto
                </button>
              )}
            </div>
          ))}

          <ScheduleTransfers
            transfers={data.transfers}
            accounts={accounts}
            canEdit={canEdit}
            busy={write.isPending}
            onNew={() => {
              setError(null);
              setDialog({ mode: 'new', initial: {} });
            }}
            onEdit={(transfer) => {
              setError(null);
              setDialog({ mode: 'edit', transfer });
            }}
            onDelete={(transfer) => {
              setError(null);
              setRemoving(transfer);
            }}
            onDone={(transfer) =>
              void run({
                path: `transfers/${transfer.id}/done`,
                method: 'POST',
                body: { movementId: transfer.candidate!.movementId },
              })
            }
          />

          <ScheduleTable
            rows={sorting.rows}
            accounts={accounts}
            canEdit={canEdit}
            withBalance={withBalance}
            busy={write.isPending}
            sorting={sorting}
            filters={filters}
            onChange={override}
          />
          {!canEdit && (
            <p className="muted small">
              Puoi consultare lo scadenzario; per riprogrammare o programmare giroconti serve la
              funzione «Programmare le scadenze».
            </p>
          )}
        </>
      )}

      {dialog && data && (
        <PlannedTransferDialog
          title={dialog.mode === 'edit' ? 'Modifica il giroconto' : 'Programma un giroconto'}
          accounts={accounts}
          initial={
            dialog.mode === 'edit'
              ? {
                  fromCode: dialog.transfer.fromCode,
                  toCode: dialog.transfer.toCode,
                  amount: dialog.transfer.amount,
                  date: dialog.transfer.date,
                  note: dialog.transfer.note,
                }
              : dialog.initial
          }
          busy={write.isPending}
          error={error}
          onSave={(body) => void saveTransfer(body)}
          onClose={() => {
            setDialog(null);
            setError(null);
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Elimina il giroconto programmato"
          confirmLabel="Elimina"
          busy={write.isPending}
          error={error}
          onConfirm={async () => {
            if (await run({ path: `transfers/${removing.id}`, method: 'DELETE' }))
              setRemoving(null);
          }}
          onCancel={() => {
            setRemoving(null);
            setError(null);
          }}
        >
          <p>
            Il giroconto di {formatMoney(removing.amount)} da {alias(removing.fromCode)} a{' '}
            {alias(removing.toCode)} non conterà più nel saldo previsto.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
